import { Log, Monitor } from '@sortlist-frontend/mlm';
import { SESSION_UUID_KEY, USER_UUID_KEY, USER_UUID_MAX_AGE_SEVEN_DAYS_SECONDS } from '@sortlist-frontend/tracking';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { PageHead } from '_components/base/head';
import { SORTLIST_COOKIES } from '_components/Briefing/utils';
import { PublicGoogleTagManager } from '_components/common';
import { PageSeoTag } from '_components/SEO';
import { extractDomainRelevantInfoFromHeaders } from '_config/domain.config';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { getServerSideTranslations } from '_core/utils/ssr';
import { toPublicQueryString } from '_core/utils/url/publicQuery';
import { getBriefingFallbackLocation } from '_features/Briefing/ssr/getBriefingFallbackLocation';

import { type BriefingProps } from '../features/Briefing';

const Briefing = dynamic<BriefingProps>(() => import('../features/Briefing').then((mod) => mod.Briefing), {
  ssr: false,
});

export default function NewProjectRoute({
  briefingProps,
  canonical,
  origin,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { t } = useTranslation(['common']);

  return (
    <AppProviders>
      <PageHead />
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" />
        <meta name="google" content="notranslate" />
      </Head>
      <PublicAppContextProvider
        canonical={canonical}
        origin={origin}
        locale={briefingProps.locale ?? 'en'}
        briefingOptions={{
          page: briefingProps.page ?? 'new-project',
        }}>
        <PublicGoogleTagManager />

        <PageSeoTag
          title={t('common:footer.descriptionTitle')}
          description={t('common:footer.description_V2')}
          nextSeoProps={{
            nofollow: true,
            noindex: true,
          }}
          canonical={canonical}
        />
        <Briefing {...briefingProps} cta={briefingProps.cta} />
      </PublicAppContextProvider>
    </AppProviders>
  );
}

type Props = {
  briefingProps: BriefingProps;
  canonical: string;
  origin: string;
};

export function getDomain(host: string | undefined) {
  return host?.includes('.') ? host.replace(/^[^.]+(?=\.)/, '') : 'localhost';
}

const SET_COOKIE_HEADER = 'Set-Cookie';

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const { briefingProps, encodedCookies, ...otherParams } = context.query;
  const { canonical, origin, locale } = extractDomainRelevantInfoFromHeaders(context.req);

  const domain = getDomain(context.req.headers.host);
  const encodedBriefingProps = Array.isArray(briefingProps) ? briefingProps[briefingProps.length - 1] : briefingProps;
  const isOpenedDirectly = encodedBriefingProps == null || encodedBriefingProps === '';
  let decodedBriefingProps: BriefingProps = isOpenedDirectly ? { page: 'new-briefing' } : {};

  if (typeof encodedCookies === 'string' && encodedCookies !== '') {
    try {
      const cookieString = atob(encodedCookies);
      const cookies = cookieString.split('; ').map((cookie) => cookie.trim());

      if (cookies.length === 0) {
        Monitor.captureMessage('[new-briefing] No cookies found in encodedCookies', {
          level: 'warning',
          extra: { encodedCookies, canonical, domain },
          tags: { page: 'new-briefing', issue: 'missing_cookies' },
        });
      }

      const foundCookies: string[] = [];

      for (const cookie of cookies) {
        if (!cookie.includes('=')) {
          Monitor.captureMessage('[new-briefing] Invalid cookie format', {
            level: 'warning',
            extra: { cookie, encodedCookies, canonical },
            tags: { page: 'new-briefing', issue: 'invalid_cookie_format' },
          });
          continue;
        }

        const cookieName = cookie.split('=')[0] as (typeof SORTLIST_COOKIES)[number];
        foundCookies.push(cookieName);

        if (cookieName === 'user_uuid') {
          context.res.appendHeader(
            SET_COOKIE_HEADER,
            `${cookie}; Max-Age=${USER_UUID_MAX_AGE_SEVEN_DAYS_SECONDS}; Domain=${domain}; SameSite=Lax`,
          );
        } else {
          context.res.appendHeader(SET_COOKIE_HEADER, `${cookie}; Domain=${domain}; SameSite=Lax`);
        }
      }

      const missingCookies = [SESSION_UUID_KEY, USER_UUID_KEY].filter(
        (cookieName) => !foundCookies.includes(cookieName),
      );

      if (missingCookies.length > 0) {
        Monitor.captureMessage('[new-briefing] Missing critical cookies', {
          level: 'warning',
          extra: {
            missingCookies,
            foundCookies,
            encodedCookies,
            canonical,
            domain,
          },
          tags: { page: 'new-briefing', issue: 'missing_critical_cookies' },
        });
      }

      const cleanUrl = new URL(canonical);
      cleanUrl.search = toPublicQueryString(otherParams);

      if (encodedBriefingProps != null) {
        cleanUrl.searchParams.append('briefingProps', encodedBriefingProps);
      }

      return {
        redirect: {
          destination: cleanUrl.toString(),
          permanent: false,
        },
      };
    } catch (error) {
      Monitor.captureException(error, {
        level: 'error',
        extra: {
          encodedCookies,
          canonical,
          domain,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        },
        tags: { page: 'new-briefing', issue: 'cookie_decode_failure' },
      });
    }
  }

  if (!isOpenedDirectly) {
    try {
      const jsonString = atob(encodedBriefingProps);
      decodedBriefingProps = JSON.parse(jsonString);
    } catch (error) {
      Log.error('Failed to decode briefingProps:', { error });
    }
  }

  const resolvedLocale = decodedBriefingProps?.locale ?? locale;
  const fallbackLocation = await getBriefingFallbackLocation({
    briefingProps: decodedBriefingProps,
    origin,
    locale: resolvedLocale,
    ipCountry: context.req.headers['cf-ipcountry'],
  });

  return {
    props: {
      briefingProps: { ...decodedBriefingProps, ...fallbackLocation, locale: resolvedLocale },
      canonical,
      origin,
      // we need common because we need companyAutocomplete key
      ...(await getServerSideTranslations(resolvedLocale, ['common', 'briefing'])),
    },
  };
};
